import React from 'react';
import { motion } from 'framer-motion';
import {
  Users, ArrowRight, ArrowLeft, CheckCircle, Star, Sparkles
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import EventsListing from './EventsListing';

const RegistrationHub = ({ onBack }) => {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="min-h-screen bg-background" data-testid="registration-hub">
      {/* Header */}
      <div className="bg-charcoal border-b border-border sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack || (() => navigate('/'))}
              className="text-muted-foreground hover:text-primary"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="font-display text-xl text-primary">Registration</h1>
              <p className="text-xs text-muted-foreground">SCC Football Academy</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          {/* Page Header */}
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl text-gradient-gold mb-4">
              Choose Your Registration
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Join the SCC Football Academy family. Sign up for academy terms, a free trial, or upcoming camps and tournaments below.
            </p>
          </motion.div>

          {/* Core options: Academy Term + Free Trial */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Academy Term Registration */}
            <motion.div variants={itemVariants}>
              <Card className="bg-gradient-card gold-border h-full hover:border-primary/50 transition-all group">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <Badge className="bg-primary/20 text-primary border-0">
                      <Users className="h-3 w-3 mr-1" />
                      Academy Term
                    </Badge>
                    <Star className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="font-display text-2xl text-primary mt-4">
                    Academy Registration
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Sign up for our weekly term-based academy training
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <FeatureRow text="Choose from multiple weekly sessions" />
                    <FeatureRow text="Development & Competitive squads" />
                    <FeatureRow text="Age groups from U6 to U18" />
                    <FeatureRow text="Volume & membership discounts" />
                  </div>

                  <div className="pt-4 border-t border-border">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-muted-foreground">Pricing</span>
                      <span className="text-sm font-medium text-primary">
                        Set by Portal · shown at review
                      </span>
                    </div>
                    <Button
                      onClick={() => navigate('/register-academy')}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                      data-testid="hub-academy-btn"
                    >
                      Register for Academy
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Free Trial */}
            <motion.div variants={itemVariants}>
              <Card className="bg-gradient-to-br from-green-900/30 to-emerald-900/20 border-green-600/50 h-full hover:border-green-500 transition-all group relative overflow-hidden">
                <div className="absolute top-4 right-4">
                  <Sparkles className="h-5 w-5 text-green-400 animate-pulse" />
                </div>
                <CardHeader>
                  <Badge className="bg-green-500/20 text-green-300 border-green-500/40 w-fit">
                    Free
                  </Badge>
                  <CardTitle className="font-display text-2xl text-green-300 mt-4">
                    Free Trial Session
                  </CardTitle>
                  <CardDescription className="text-green-100/70">
                    Try us out before committing — no payment required
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <FeatureRow text="One free trial session" tone="green" />
                    <FeatureRow text="Coaches assess your child's level" tone="green" />
                    <FeatureRow text="Match with the right age & ability group" tone="green" />
                    <FeatureRow text="Friendly, low-pressure introduction" tone="green" />
                  </div>

                  <div className="pt-4 border-t border-green-600/30">
                    <Button
                      onClick={() => navigate('/free-trial')}
                      className="w-full bg-green-500 text-black hover:bg-green-400 font-bold"
                      data-testid="hub-free-trial-btn"
                    >
                      Book Free Trial
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Dynamic Camps + Tournaments — pulled from Portal */}
          <motion.div variants={itemVariants}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-display text-2xl text-primary">Camps & Tournaments</h3>
                <p className="text-sm text-muted-foreground">Upcoming events — open to members and non-members</p>
              </div>
            </div>
            <EventsListing emptyMessage="No camps or tournaments scheduled right now. Check back soon!" />
          </motion.div>

          {/* Help Section */}
          <motion.div variants={itemVariants} className="mt-12">
            <Card className="bg-charcoal border-border">
              <CardContent className="p-6 text-center">
                <p className="text-muted-foreground">
                  Need help choosing? Chat with our AI assistant or contact us at{' '}
                  <a href="mailto:academy@sccfootball.com" className="text-primary hover:underline">
                    academy@sccfootball.com
                  </a>
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

function FeatureRow({ text, tone = 'default' }) {
  const colorClass = tone === 'green' ? 'text-green-400' : 'text-success';
  return (
    <div className="flex items-center gap-2 text-sm text-card-foreground">
      <CheckCircle className={`h-4 w-4 ${colorClass}`} />
      {text}
    </div>
  );
}

export default RegistrationHub;
